import { createLogger } from "@/lib/logger";
import {
  supportsBufferedVoiceNotes,
  supportsVoiceNotePronunciationAnalysis,
} from "@/lib/voiceNoteCapabilities";
import {
  VOICE_NOTE_PRONUNCIATION_TOPIC,
  type VoiceNotePronunciationProvider,
  type VoiceNotePronunciationState,
  parseVoiceNotePronunciationPacket,
} from "@/lib/voiceNotePronunciation";
import {
  type VoiceNoteFailureStage,
  type VoiceNoteRecoveryAction,
  VoiceNoteRejectedError,
  describeVoiceNoteFailure,
} from "@/lib/voiceNoteRecovery";
import {
  ConnectionState,
  type DataPacket_Kind,
  type Participant,
  type Room,
  RoomEvent,
} from "livekit-client";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useVoiceNoteAudioLevel } from "./useVoiceNoteAudioLevel";

export { getVoiceNoteAudioLevel } from "./useVoiceNoteAudioLevel";

const logger = createLogger("useVoiceNoteTurn");
const VOICE_NOTE_AUDIO_TOPIC = "chickytutor.voice-note.audio.v1";
const VOICE_NOTE_PROTOCOL_VERSION = "1";
const PRONUNCIATION_ANALYSIS_PREFERENCE_KEY = "chickytutor:pronunciation-analysis-enabled";

export type VoiceNoteTurnState =
  | "idle"
  | "starting"
  | "recording"
  | "reviewing"
  | "committing"
  | "cancelling"
  | "error";

interface UseVoiceNoteTurnOptions {
  room: Room | null;
  agentIdentity?: string;
  enabled: boolean;
  isConnected: boolean;
  isMuted: boolean;
  setMuted?: (muted: boolean) => void | Promise<void>;
  maxDurationSeconds?: number;
  reviewDurationSeconds?: number;
}

interface VoiceNoteRpcResponse {
  ok?: boolean;
  state?: string;
  error?: string;
  transcript?: string;
  voiceNoteId?: string;
  audioTopic?: string;
  protocolVersion?: string;
  pronunciationAnalysis?: {
    status?: "pending";
    provider?: VoiceNotePronunciationProvider;
  };
}

function createRequestPayload(extra: Record<string, unknown> = {}) {
  const requestId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `voice-note-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return JSON.stringify({ requestId, sentAtMs: Date.now(), ...extra });
}

function parseRpcResponse(value: string): VoiceNoteRpcResponse {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof (parsed as VoiceNoteRpcResponse).ok !== "boolean"
    )
      return { ok: false, error: "invalid_response" };
    return parsed as VoiceNoteRpcResponse;
  } catch {
    return { ok: false, error: "invalid_response" };
  }
}

export function useVoiceNoteTurn({
  room,
  agentIdentity,
  enabled,
  isConnected,
  isMuted,
  setMuted,
  maxDurationSeconds = 60,
  reviewDurationSeconds = 30,
}: UseVoiceNoteTurnOptions) {
  const posthog = usePostHog();
  const [state, setState] = useState<VoiceNoteTurnState>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recoveryAction, setRecoveryAction] = useState<VoiceNoteRecoveryAction>("record_again");
  const recoveryActionRef = useRef<VoiceNoteRecoveryAction>("record_again");
  const retryDeadlineRef = useRef<number | null>(null);
  const attemptRef = useRef(1);
  const quietWarningReportedRef = useRef(false);
  const recordingMetadataRef = useRef<{ bytes?: number; mimeType?: string }>({});
  const [isSupported, setIsSupported] = useState(false);
  const [pronunciationAnalysisAvailable, setPronunciationAnalysisAvailable] = useState(false);
  const [pronunciationAnalysisEnabled, setPronunciationAnalysisEnabledState] = useState(true);
  const [pronunciationAnalysis, setPronunciationAnalysis] =
    useState<VoiceNotePronunciationState | null>(null);
  const stateRef = useRef<VoiceNoteTurnState>("idle");
  const mutedRef = useRef(isMuted);
  const mutedBeforeVoiceNoteRef = useRef(isMuted);
  const startedAtRef = useRef<number | null>(null);
  const reviewDeadlineRef = useRef<number | null>(null);
  const operationRef = useRef(0);
  const activeTargetRef = useRef<{ room: Room; identity: string } | null>(null);
  const pendingRecordingRef = useRef<{
    blob: Promise<Blob | null>;
    durationSeconds: number;
  } | null>(null);
  const activeVoiceNoteIdRef = useRef<string | null>(null);
  const originalVoiceNoteIdRef = useRef<string | null>(null);
  const latestCommittedVoiceNoteIdRef = useRef<string | null>(null);
  const dismissedPronunciationIdsRef = useRef(new Set<string>());
  const pronunciationAnalysisEnabledRef = useRef(true);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
  const recordingChunksRef = useRef<BlobPart[]>([]);
  const roomRef = useRef(room);
  const agentIdentityRef = useRef(agentIdentity);
  const setMutedRef = useRef(setMuted);
  const {
    audioLevel,
    showQuietWarning,
    evidence: audioEvidence,
  } = useVoiceNoteAudioLevel(recordingStream);

  const reportFailure = useCallback(
    (cause: unknown, phase: string, stage: VoiceNoteFailureStage, hasDraft = false) => {
      const failure = describeVoiceNoteFailure(cause, stage, hasDraft);
      recoveryActionRef.current = failure.action;
      setRecoveryAction(failure.action);
      posthog?.capture("voice_note_record_failed", {
        phase,
        reason: failure.reason,
        stage,
        rpc_code: failure.rpc_code,
        recovery_action: failure.action,
        voice_note_id: activeVoiceNoteIdRef.current,
        original_voice_note_id: originalVoiceNoteIdRef.current,
        attempt: attemptRef.current,
        duration_seconds: pendingRecordingRef.current?.durationSeconds,
        audio_bytes: recordingMetadataRef.current.bytes,
        audio_mime_type: recordingMetadataRef.current.mimeType,
        audio_monitoring_available: audioEvidence.current.available,
        audio_detected: audioEvidence.current.detectedAudio,
      });
      return failure;
    },
    [posthog, audioEvidence]
  );

  useEffect(() => {
    if (state !== "recording" || !showQuietWarning || quietWarningReportedRef.current) return;
    quietWarningReportedRef.current = true;
    posthog?.capture("voice_note_quiet_warning_shown", {
      voice_note_id: activeVoiceNoteIdRef.current,
      original_voice_note_id: originalVoiceNoteIdRef.current,
      elapsed_seconds: elapsedSeconds,
    });
  }, [state, showQuietWarning, posthog, elapsedSeconds]);

  useEffect(() => {
    roomRef.current = room;
    agentIdentityRef.current = agentIdentity;
    setMutedRef.current = setMuted;
  }, [agentIdentity, room, setMuted]);

  useEffect(() => {
    mutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(PRONUNCIATION_ANALYSIS_PREFERENCE_KEY);
      if (stored === "false") {
        pronunciationAnalysisEnabledRef.current = false;
        setPronunciationAnalysisEnabledState(false);
      }
    } catch {
      // A blocked storage API leaves the session-scoped default enabled.
    }
  }, []);

  useEffect(() => {
    const syncCapability = () => {
      const participant =
        room && agentIdentity ? room.remoteParticipants.get(agentIdentity) : undefined;
      setIsSupported(supportsBufferedVoiceNotes(participant?.attributes));
      setPronunciationAnalysisAvailable(
        supportsVoiceNotePronunciationAnalysis(participant?.attributes)
      );
    };
    syncCapability();
    if (!room) return;

    const handleAttributesChanged = (
      _changedAttributes: Record<string, string>,
      participant: Participant
    ) => {
      if (participant.identity === agentIdentity) syncCapability();
    };
    const handleParticipantChanged = (participant: Participant) => {
      if (participant.identity === agentIdentity) syncCapability();
    };

    room.on(RoomEvent.ParticipantAttributesChanged, handleAttributesChanged);
    room.on(RoomEvent.ParticipantConnected, handleParticipantChanged);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantChanged);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, handleAttributesChanged);
      room.off(RoomEvent.ParticipantConnected, handleParticipantChanged);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantChanged);
    };
  }, [agentIdentity, room]);

  useEffect(() => {
    if (!room) return;
    const handlePronunciationPacket = (
      payload: Uint8Array,
      participant?: Participant,
      _kind?: DataPacket_Kind,
      topic?: string
    ) => {
      if (topic !== VOICE_NOTE_PRONUNCIATION_TOPIC || participant?.identity !== agentIdentity) {
        return;
      }
      const packet = parseVoiceNotePronunciationPacket(payload);
      if (
        !packet ||
        !pronunciationAnalysisEnabledRef.current ||
        packet.voiceNoteId !== latestCommittedVoiceNoteIdRef.current ||
        dismissedPronunciationIdsRef.current.has(packet.voiceNoteId)
      ) {
        return;
      }
      setPronunciationAnalysis(packet);
    };

    room.on(RoomEvent.DataReceived, handlePronunciationPacket);
    return () => {
      room.off(RoomEvent.DataReceived, handlePronunciationPacket);
    };
  }, [agentIdentity, room]);

  const transition = useCallback((next: VoiceNoteTurnState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const isCurrentOperation = useCallback(
    (operation: number, expectedState?: VoiceNoteTurnState) => {
      const target = activeTargetRef.current;
      return (
        operation === operationRef.current &&
        (!expectedState || stateRef.current === expectedState) &&
        target?.room === roomRef.current &&
        target?.identity === agentIdentityRef.current &&
        target?.room.state === ConnectionState.Connected
      );
    },
    []
  );

  const applyMuted = useCallback(async (muted: boolean) => {
    const setter = setMutedRef.current;
    if (!setter || mutedRef.current === muted) return;
    await setter(muted);
    mutedRef.current = muted;
  }, []);

  const performRpc = useCallback(
    async (
      method: string,
      payload: Record<string, unknown> = {}
    ): Promise<VoiceNoteRpcResponse> => {
      const activeRoom = roomRef.current;
      const destinationIdentity = agentIdentityRef.current;
      if (!activeRoom || !destinationIdentity) {
        throw new Error("agent_not_ready");
      }

      const raw = await activeRoom.localParticipant.performRpc({
        destinationIdentity,
        method,
        payload: createRequestPayload(payload),
      });
      const response = parseRpcResponse(raw);
      if (!response.ok) {
        if (response.error === "invalid_response") throw new Error("invalid_response");
        throw new VoiceNoteRejectedError(
          typeof response.error === "string" ? response.error : `${method}_failed`
        );
      }
      return response;
    },
    []
  );

  const stopLocalRecording = useCallback(async (): Promise<Blob | null> => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return null;
    const chunks = recordingChunksRef.current;
    try {
      return await new Promise<Blob>((resolve, reject) => {
        const finish = () => resolve(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
        recorder.onstop = finish;
        recorder.onerror = () => reject(new Error("recording_failed"));
        if (recorder.state === "inactive") finish();
        else recorder.stop();
      });
    } finally {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      chunks.length = 0;
      // A late stop event must not clear a subsequent recorder's tracks or data.
      if (mediaRecorderRef.current === recorder) {
        mediaRecorderRef.current = null;
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        setRecordingStream(null);
      }
    }
  }, []);

  const prepareRecording = useCallback(() => {
    if (!pendingRecordingRef.current) {
      pendingRecordingRef.current = {
        durationSeconds:
          startedAtRef.current === null
            ? 0
            : Math.max(0, (Date.now() - startedAtRef.current) / 1000),
        blob: stopLocalRecording(),
      };
    }
    return pendingRecordingRef.current;
  }, [stopLocalRecording]);

  const cancelLocalRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder) recorder.ondataavailable = null;
    try {
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
    } catch {
      // Ignore recorder cleanup errors during cancel/reconnect recovery.
    }
    mediaRecorderRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    setRecordingStream(null);
    recordingChunksRef.current.length = 0;
    pendingRecordingRef.current = null;
    reviewDeadlineRef.current = null;
    retryDeadlineRef.current = null;
  }, []);

  const startLocalRecording = useCallback(
    async (operation: number) => {
      if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        throw new Error("recording_not_supported");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!isCurrentOperation(operation)) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      mediaStreamRef.current = stream;
      const mimeType = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ].find((candidate) => MediaRecorder.isTypeSupported(candidate));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks: BlobPart[] = [];
      recordingChunksRef.current = chunks;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      mediaStreamRef.current = stream;
      setRecordingStream(stream);
      mediaRecorderRef.current = recorder;
      recorder.start();
    },
    [isCurrentOperation]
  );

  const stopLiveKitMicrophoneTrack = useCallback(async () => {
    const activeRoom = roomRef.current;
    if (!activeRoom) return;
    await activeRoom.localParticipant.setMicrophoneEnabled(false, undefined, {
      stopMicTrackOnMute: true,
    } as Parameters<typeof activeRoom.localParticipant.setMicrophoneEnabled>[2]);
    mutedRef.current = true;
    const setter = setMutedRef.current;
    if (setter) {
      await setter(true);
    }
  }, []);

  const sendRecording = useCallback(
    async (blob: Blob, durationSeconds: number, operation: number) => {
      const activeRoom = roomRef.current;
      const destinationIdentity = agentIdentityRef.current;
      const voiceNoteId = activeVoiceNoteIdRef.current;
      if (!activeRoom || !destinationIdentity || !voiceNoteId) {
        throw new Error("voice_note_not_ready");
      }
      const bytes = new Uint8Array(await blob.arrayBuffer());
      if (!isCurrentOperation(operation)) throw new Error("voice_note_interrupted");
      const writer = await activeRoom.localParticipant.streamBytes({
        name: `${voiceNoteId}.webm`,
        topic: VOICE_NOTE_AUDIO_TOPIC,
        mimeType: blob.type || "audio/webm",
        totalSize: bytes.byteLength,
        destinationIdentities: [destinationIdentity],
        attributes: {
          voiceNoteId,
          protocolVersion: VOICE_NOTE_PROTOCOL_VERSION,
          durationSeconds: String(Number(durationSeconds.toFixed(3))),
        },
      });
      try {
        if (!isCurrentOperation(operation)) throw new Error("voice_note_interrupted");
        await writer.write(bytes);
      } finally {
        await writer.close();
      }
      return bytes.byteLength;
    },
    [isCurrentOperation]
  );

  const restorePreviousMuteState = useCallback(async () => {
    await applyMuted(mutedBeforeVoiceNoteRef.current);
  }, [applyMuted]);

  const cancelServerTurnBestEffort = useCallback(async () => {
    if (roomRef.current?.state !== ConnectionState.Connected) return;
    try {
      await performRpc("voice_note.cancel", { voiceNoteId: activeVoiceNoteIdRef.current });
    } catch {
      // Server watchdog/disconnect cleanup owns recovery if the agent RPC is unavailable.
    }
  }, [performRpc]);

  const start = useCallback(async () => {
    if (stateRef.current !== "idle" && stateRef.current !== "error") return;
    if (!enabled || !isSupported || !isConnected || !roomRef.current || !agentIdentityRef.current) {
      setError(reportFailure(new Error("agent_not_ready"), "start", "start").message);
      transition("error");
      return;
    }
    const recovering = stateRef.current === "error";
    if (recovering && recoveryActionRef.current !== "record_again") return;
    const operation = ++operationRef.current;
    activeTargetRef.current = { room: roomRef.current!, identity: agentIdentityRef.current! };
    transition("starting");

    if (recovering) {
      cancelLocalRecording();
      try {
        await cancelServerTurnBestEffort();
        if (!isCurrentOperation(operation)) return;
        await restorePreviousMuteState();
      } catch (cause) {
        if (!isCurrentOperation(operation)) return;
        logger.error("Failed to restore microphone before voice-note retry", {
          error: cause instanceof Error ? cause.message : String(cause),
        });
        const failure = reportFailure(cause, "start", "cancel");
        setError(failure.message);
        transition("error");
        return;
      }
    }

    if (!isCurrentOperation(operation)) return;
    mutedBeforeVoiceNoteRef.current = mutedRef.current;
    pendingRecordingRef.current = null;
    reviewDeadlineRef.current = null;
    retryDeadlineRef.current = null;
    attemptRef.current = 1;
    quietWarningReportedRef.current = false;
    recordingMetadataRef.current = {};
    audioEvidence.current = { available: false, monitoring: false, detectedAudio: false };
    activeVoiceNoteIdRef.current = null;
    originalVoiceNoteIdRef.current = null;
    setError(null);
    setElapsedSeconds(0);
    transition("starting");

    try {
      const startResponse = await performRpc("voice_note.start");
      if (!isCurrentOperation(operation)) return;
      if (!startResponse.voiceNoteId) {
        throw new Error("missing_voice_note_id");
      }
      activeVoiceNoteIdRef.current = startResponse.voiceNoteId;
      originalVoiceNoteIdRef.current = startResponse.voiceNoteId;
      latestCommittedVoiceNoteIdRef.current = null;
      setPronunciationAnalysis(null);
      await stopLiveKitMicrophoneTrack();
      if (!isCurrentOperation(operation)) return;
      await startLocalRecording(operation);
      if (!isCurrentOperation(operation)) return;
      startedAtRef.current = Date.now();
      reviewDeadlineRef.current =
        startedAtRef.current +
        (Math.max(1, maxDurationSeconds) + Math.max(1, reviewDurationSeconds)) * 1000;
      transition("recording");
      posthog?.capture("voice_note_record_started");
    } catch (cause) {
      if (!isCurrentOperation(operation)) return;
      logger.error("Failed to start voice-note turn", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
      await cancelServerTurnBestEffort();
      if (!isCurrentOperation(operation)) return;
      cancelLocalRecording();
      try {
        await restorePreviousMuteState();
      } catch {
        // Preserve the original start error in the UI.
      }
      if (!isCurrentOperation(operation)) return;
      setError(reportFailure(cause, "start", "start").message);
      transition("error");
    }
  }, [
    cancelServerTurnBestEffort,
    cancelLocalRecording,
    audioEvidence,
    reportFailure,
    enabled,
    isSupported,
    isConnected,
    isCurrentOperation,
    maxDurationSeconds,
    reviewDurationSeconds,
    performRpc,
    posthog,
    restorePreviousMuteState,
    startLocalRecording,
    stopLiveKitMicrophoneTrack,
    transition,
  ]);

  const cancel = useCallback(
    async (reason: "user" | "timeout" = "user") => {
      if (!["starting", "recording", "reviewing", "error"].includes(stateRef.current)) return;
      const fromReview = stateRef.current === "reviewing";
      const durationSeconds =
        pendingRecordingRef.current?.durationSeconds ??
        (startedAtRef.current === null
          ? 0
          : Math.max(0, (Date.now() - startedAtRef.current) / 1000));
      const operation = ++operationRef.current;
      transition("cancelling");
      cancelLocalRecording();
      try {
        await cancelServerTurnBestEffort();
        if (!isCurrentOperation(operation)) return;
        await restorePreviousMuteState();
        if (!isCurrentOperation(operation)) return;
        activeVoiceNoteIdRef.current = null;
        startedAtRef.current = null;
        setElapsedSeconds(0);
        setError(null);
        transition("idle");
        posthog?.capture("voice_note_record_cancelled", {
          duration_seconds: Number(durationSeconds.toFixed(2)),
          reason,
          from_review: fromReview,
        });
      } catch (cause) {
        if (!isCurrentOperation(operation)) return;
        logger.error("Failed to cancel voice-note turn", {
          error: cause instanceof Error ? cause.message : String(cause),
        });
        setError(reportFailure(cause, "cancel", "cancel").message);
        transition("error");
      }
    },
    [
      cancelServerTurnBestEffort,
      cancelLocalRecording,
      reportFailure,
      isCurrentOperation,
      posthog,
      restorePreviousMuteState,
      transition,
    ]
  );

  const stopForReview = useCallback(async () => {
    if (stateRef.current !== "recording") return;
    if (reviewDeadlineRef.current !== null && Date.now() >= reviewDeadlineRef.current) {
      await cancel("timeout");
      return;
    }
    const operation = operationRef.current;
    const draft = prepareRecording();
    setElapsedSeconds(Math.floor(draft.durationSeconds));
    transition("reviewing");
    posthog?.capture("voice_note_recording_limit_reached", {
      duration_seconds: Number(draft.durationSeconds.toFixed(2)),
    });
    try {
      const blob = await draft.blob;
      if (!isCurrentOperation(operation, "reviewing")) return;
      if (!blob?.size) throw new Error("empty_audio");
    } catch (cause) {
      // Send may already own this same stop promise; only its owner handles failure.
      if (!isCurrentOperation(operation, "reviewing")) return;
      await cancel();
      if (operationRef.current !== operation + 1) return;
      setError(reportFailure(cause, "review", "review").message);
      transition("error");
      logger.error("Failed to stop voice-note recording for review", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
    }
  }, [cancel, isCurrentOperation, posthog, prepareRecording, reportFailure, transition]);

  const expireRetry = useCallback(() => {
    const failure = describeVoiceNoteFailure(new Error("retry_expired"), "recording");
    recoveryActionRef.current = failure.action;
    setRecoveryAction(failure.action);
    cancelLocalRecording();
    setError(failure.message);
    posthog?.capture("voice_note_commit_retry_outcome", {
      voice_note_id: activeVoiceNoteIdRef.current,
      original_voice_note_id: originalVoiceNoteIdRef.current,
      outcome: "expired",
      attempt: attemptRef.current,
    });
  }, [cancelLocalRecording, posthog]);

  const commit = useCallback(async () => {
    const retry =
      stateRef.current === "error" &&
      recoveryActionRef.current === "retry_send" &&
      Boolean(pendingRecordingRef.current);
    if (!retry && !["recording", "reviewing"].includes(stateRef.current)) return;
    const deadline = retry ? retryDeadlineRef.current : reviewDeadlineRef.current;
    if (deadline !== null && Date.now() >= deadline) {
      if (!retry) {
        await cancel("timeout");
        return;
      }
      expireRetry();
      return;
    }
    const fromReview = stateRef.current === "reviewing";
    const operation = operationRef.current;
    const draft = prepareRecording();
    const durationSeconds = draft.durationSeconds;
    if (retry) attemptRef.current += 1;
    transition("committing");
    setError(null);
    let stage: VoiceNoteFailureStage = "recording";
    let recording: Blob | null = null;
    const attemptStartedAt = Date.now();
    try {
      recording = await draft.blob;
      if (!isCurrentOperation(operation)) return;
      if (!recording?.size) throw new Error("empty_audio");
      recordingMetadataRef.current = { bytes: recording.size, mimeType: recording.type };
      if (retry) {
        stage = "retry_prepare";
        // Cancel must be acknowledged before reserving a fresh note: old workers
        // do not validate a note ID on cancel, so a background cancel is unsafe.
        await performRpc("voice_note.cancel", { voiceNoteId: activeVoiceNoteIdRef.current });
        if (!isCurrentOperation(operation)) return;
        const reserved = await performRpc("voice_note.start");
        if (!isCurrentOperation(operation)) return;
        if (!reserved.voiceNoteId) throw new Error("missing_voice_note_id");
        activeVoiceNoteIdRef.current = reserved.voiceNoteId;
        await stopLiveKitMicrophoneTrack();
        if (!isCurrentOperation(operation)) return;
      }
      stage = "upload";
      const bytes = await sendRecording(recording, durationSeconds, operation);
      if (!isCurrentOperation(operation)) return;
      const voiceNoteId = activeVoiceNoteIdRef.current;
      if (!voiceNoteId) throw new Error("missing_voice_note_id");
      latestCommittedVoiceNoteIdRef.current = voiceNoteId;
      stage = "commit";
      const response = await performRpc("voice_note.commit", {
        voiceNoteId,
        durationSeconds: Number(durationSeconds.toFixed(3)),
        bytes,
        pronunciationAnalysisEnabled: pronunciationAnalysisEnabledRef.current,
      });
      if (!isCurrentOperation(operation)) return;
      // Acknowledged acceptance is a sent note even if microphone recovery fails.
      posthog?.capture("voice_note_record_committed", {
        voice_note_id: voiceNoteId,
        original_voice_note_id: originalVoiceNoteIdRef.current,
        duration_seconds: Number(durationSeconds.toFixed(2)),
        from_review: fromReview,
        attempt: attemptRef.current,
        is_retry: retry,
      });
      if (retry)
        posthog?.capture("voice_note_commit_retry_outcome", {
          voice_note_id: voiceNoteId,
          original_voice_note_id: originalVoiceNoteIdRef.current,
          outcome: "sent",
          attempt: attemptRef.current,
          elapsed_ms: Date.now() - attemptStartedAt,
        });
      pendingRecordingRef.current = null;
      reviewDeadlineRef.current = null;
      retryDeadlineRef.current = null;
      if (!isCurrentOperation(operation)) return;
      const pronunciationProvider = response.pronunciationAnalysis?.provider;
      if (
        pronunciationAnalysisEnabledRef.current &&
        response.pronunciationAnalysis?.status === "pending" &&
        pronunciationProvider
      ) {
        setPronunciationAnalysis((current) =>
          current?.voiceNoteId === voiceNoteId && current.status !== "pending"
            ? current
            : {
                voiceNoteId,
                provider: pronunciationProvider,
                status: "pending",
              }
        );
      }
      stage = "microphone_restore";
      await restorePreviousMuteState();
      if (!isCurrentOperation(operation)) return;
      activeVoiceNoteIdRef.current = null;
      startedAtRef.current = null;
      setElapsedSeconds(0);
      transition("idle");
    } catch (cause) {
      if (!isCurrentOperation(operation)) return;
      const failure = reportFailure(
        cause,
        stage === "microphone_restore" ? "restore" : "commit",
        stage,
        Boolean(recording?.size)
      );
      if (retry && stage !== "microphone_restore")
        posthog?.capture("voice_note_commit_retry_outcome", {
          voice_note_id: activeVoiceNoteIdRef.current,
          original_voice_note_id: originalVoiceNoteIdRef.current,
          outcome: "failed",
          reason: failure.reason,
          stage,
          attempt: attemptRef.current,
          elapsed_ms: Date.now() - attemptStartedAt,
        });
      if (failure.action === "retry_send") {
        retryDeadlineRef.current ??= Date.now() + 30_000;
        reviewDeadlineRef.current = null;
        await cancelServerTurnBestEffort();
      } else {
        cancelLocalRecording();
      }
      if (!isCurrentOperation(operation)) return;
      if (stage !== "microphone_restore") {
        latestCommittedVoiceNoteIdRef.current = null;
        setPronunciationAnalysis(null);
        try {
          await restorePreviousMuteState();
        } catch {
          /* Keep original failure and explicit recovery action. */
        }
      }
      if (!isCurrentOperation(operation)) return;
      logger.error("Voice-note operation failed", {
        reason: failure.reason,
        stage,
        rpc_code: failure.rpc_code,
      });
      setError(failure.message);
      transition("error");
    }
  }, [
    cancel,
    cancelLocalRecording,
    cancelServerTurnBestEffort,
    expireRetry,
    isCurrentOperation,
    performRpc,
    posthog,
    prepareRecording,
    reportFailure,
    restorePreviousMuteState,
    sendRecording,
    stopLiveKitMicrophoneTrack,
    transition,
  ]);

  useEffect(() => {
    if (state !== "error" || recoveryAction !== "retry_send" || retryDeadlineRef.current === null)
      return;
    const expire = () => {
      if (
        stateRef.current !== "error" ||
        retryDeadlineRef.current === null ||
        Date.now() < retryDeadlineRef.current
      )
        return;
      expireRetry();
    };
    const timer = window.setTimeout(expire, Math.max(0, retryDeadlineRef.current - Date.now()));
    document.addEventListener("visibilitychange", expire);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", expire);
    };
  }, [state, recoveryAction, expireRetry]);

  useEffect(() => {
    if (state !== "recording") return;

    const updateElapsed = () => {
      if (stateRef.current !== "recording" || startedAtRef.current === null) return;
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
      if (Date.now() >= startedAtRef.current + Math.max(1, maxDurationSeconds) * 1000) {
        void stopForReview();
      }
    };
    updateElapsed();
    const interval = window.setInterval(updateElapsed, 250);
    const autoStop = window.setTimeout(
      () => void stopForReview(),
      Math.max(
        0,
        (startedAtRef.current ?? Date.now()) + Math.max(1, maxDurationSeconds) * 1000 - Date.now()
      )
    );
    document.addEventListener("visibilitychange", updateElapsed);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(autoStop);
      document.removeEventListener("visibilitychange", updateElapsed);
    };
  }, [stopForReview, maxDurationSeconds, state]);

  useEffect(() => {
    if (state !== "reviewing" || reviewDeadlineRef.current === null) return;
    const expire = () => {
      if (
        stateRef.current === "reviewing" &&
        reviewDeadlineRef.current !== null &&
        Date.now() >= reviewDeadlineRef.current
      )
        void cancel("timeout");
    };
    const timer = window.setTimeout(expire, Math.max(0, reviewDeadlineRef.current - Date.now()));
    document.addEventListener("visibilitychange", expire);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", expire);
    };
  }, [cancel, state]);

  useEffect(() => {
    if (
      (isConnected &&
        activeTargetRef.current?.room === room &&
        activeTargetRef.current?.identity === agentIdentity) ||
      (!["starting", "recording", "reviewing", "committing", "cancelling"].includes(
        stateRef.current
      ) &&
        !pendingRecordingRef.current)
    ) {
      return;
    }
    ++operationRef.current;
    cancelLocalRecording();
    void stopLiveKitMicrophoneTrack();
    setError(reportFailure(new Error("connection_changed"), "connection", "connection").message);
    transition("error");
  }, [
    agentIdentity,
    room,
    reportFailure,
    cancelLocalRecording,
    isConnected,
    stopLiveKitMicrophoneTrack,
    transition,
  ]);

  useEffect(() => {
    return () => {
      ++operationRef.current;
      if (
        ["starting", "recording", "reviewing", "committing", "cancelling"].includes(
          stateRef.current
        ) ||
        pendingRecordingRef.current
      ) {
        cancelLocalRecording();
        void stopLiveKitMicrophoneTrack();
        const activeRoom = roomRef.current;
        const destinationIdentity = agentIdentityRef.current;
        if (activeRoom && destinationIdentity && activeRoom.state === ConnectionState.Connected) {
          void activeRoom.localParticipant
            .performRpc({
              destinationIdentity,
              method: "voice_note.cancel",
              payload: createRequestPayload({ voiceNoteId: activeVoiceNoteIdRef.current }),
            })
            .catch(() => undefined);
        }
      }
    };
  }, [cancelLocalRecording, stopLiveKitMicrophoneTrack]);

  const reset = useCallback(async () => {
    if (stateRef.current !== "error") return;
    const operation = ++operationRef.current;
    if (!activeTargetRef.current) {
      setError(null);
      transition("idle");
      return;
    }
    transition("cancelling");
    try {
      cancelLocalRecording();
      await cancelServerTurnBestEffort();
      if (operation !== operationRef.current) return;
      await restorePreviousMuteState();
      if (operation !== operationRef.current) return;
      setError(null);
      setElapsedSeconds(0);
      startedAtRef.current = null;
      activeVoiceNoteIdRef.current = null;
      transition("idle");
    } catch (cause) {
      if (operation !== operationRef.current) return;
      setError(reportFailure(cause, "cancel", "cancel").message);
      transition("error");
    }
  }, [
    cancelServerTurnBestEffort,
    cancelLocalRecording,
    restorePreviousMuteState,
    reportFailure,
    transition,
  ]);

  const dismissPronunciationAnalysis = useCallback(() => {
    setPronunciationAnalysis((current) => {
      if (current) dismissedPronunciationIdsRef.current.add(current.voiceNoteId);
      return null;
    });
  }, []);

  const setPronunciationAnalysisEnabled = useCallback((nextEnabled: boolean) => {
    pronunciationAnalysisEnabledRef.current = nextEnabled;
    setPronunciationAnalysisEnabledState(nextEnabled);
    if (!nextEnabled) {
      latestCommittedVoiceNoteIdRef.current = null;
      setPronunciationAnalysis(null);
    }
    try {
      window.localStorage.setItem(PRONUNCIATION_ANALYSIS_PREFERENCE_KEY, String(nextEnabled));
    } catch {
      // The preference remains session-scoped when storage is unavailable.
    }
  }, []);

  return {
    state,
    elapsedSeconds,
    error,
    audioLevel,
    showQuietWarning: state === "recording" && showQuietWarning,
    recoveryAction,
    pronunciationAnalysis,
    pronunciationAnalysisAvailable,
    pronunciationAnalysisEnabled,
    isSupported,
    isAvailable: Boolean(
      enabled && isSupported && isConnected && room && agentIdentity && setMuted
    ),
    isActive: ["starting", "recording", "reviewing", "committing", "cancelling"].includes(state),
    start,
    commit,
    cancel,
    reset,
    dismissPronunciationAnalysis,
    setPronunciationAnalysisEnabled,
  };
}
